import { z } from 'zod'

import { dateUtil, isNull } from '../../util'
import { csvColumnLabelFor } from '../csvColumnLabel'

// 剪断証明書CSVの日本語ヘッダ → フィールドのマッピング
// 親（剪断証明書）レベル: 発行会社名 / 発行日 / 剪断証明書番号 / 工事名
// 明細（個体番号単位）レベル: 規格 / 板厚 / 重量 / 証明書番号 / 個体番号 / メーカー名
export const shearingCertificateCsvColumnMapper = {
  issuingCompanyName: '発行会社名',
  issuedDate: '発行日',
  certificateNo: '剪断証明書番号',
  constructionName: '工事名',
  steelType: '規格',
  thickness: '板厚',
  weight: '重量',
  no: '証明書番号',
  individualNumber: '個体番号',
  manufacturerName: 'メーカー名',
}

// 同一の剪断証明書（親）としてグルーピングするためのキー。
// 剪断証明書番号は任意項目のため、未入力の場合は 発行会社名+発行日+工事名 の複合キーで代替する。
export function shearingCertificateGroupKey(row: {
  certificateNo: string | null
  issuingCompanyName: string
  issuedDate: string
  constructionName: string
}) {
  const certificateNo = row.certificateNo?.trim() ?? ''
  if (certificateNo !== '') {
    return `no:${certificateNo}`
  }
  return `composite:${row.issuingCompanyName}${row.issuedDate}${row.constructionName}`
}

export function validateAndNormalizeShearingCertificateCsvRecords({
  record,
  index,
}: {
  record: Record<string, string | number | null>
  index: number
}) {
  // すべて空行だったらスキップ
  if (Object.values(record).every((x) => x === '')) {
    return {
      success: false as const,
      isEmptyLine: true,
      message: `空行です。`,
      record,
      index,
    }
  }

  // 必要なデータのみを一旦抽出する
  const data = {
    issuingCompanyName: record[shearingCertificateCsvColumnMapper.issuingCompanyName],
    issuedDate: record[shearingCertificateCsvColumnMapper.issuedDate],
    certificateNo: record[shearingCertificateCsvColumnMapper.certificateNo],
    constructionName: record[shearingCertificateCsvColumnMapper.constructionName],
    steelType: record[shearingCertificateCsvColumnMapper.steelType],
    thickness: record[shearingCertificateCsvColumnMapper.thickness],
    weight: record[shearingCertificateCsvColumnMapper.weight],
    no: record[shearingCertificateCsvColumnMapper.no],
    individualNumber: record[shearingCertificateCsvColumnMapper.individualNumber],
    manufacturerName: record[shearingCertificateCsvColumnMapper.manufacturerName],
  }

  const schema = z.object({
    issuingCompanyName: z.string(),
    issuedDate: z.string(),
    // 剪断証明書番号は任意項目。空欄なら null として扱う
    certificateNo: z
      .string()
      .optional()
      .transform((x) => {
        if (isNull(x)) {
          return null
        }
        const trimmed = x.trim()
        return trimmed === '' ? null : trimmed
      }),
    constructionName: z.string().optional(),
    steelType: z.string().optional(),
    thickness: z.string().optional(),
    no: z.string().optional(),
    individualNumber: z.string().optional(),
    manufacturerName: z.string().optional(),
    // 重量はミルシート同様、整数のみ。空欄なら null（集計不要な鋼材）
    weight: z
      .string()
      .transform((x, ctx) => {
        const trimmed = x.trim()
        if (trimmed === '') {
          return null
        }
        if (!/^[0-9,]+$/.test(trimmed)) {
          ctx.addIssue({
            code: 'custom',
            message: '整数で入力してください。',
          })
          return z.NEVER
        }
        const parsed = Number(trimmed.replace(/,/g, ''))
        if (!Number.isInteger(parsed)) {
          ctx.addIssue({
            code: 'custom',
            message: '整数で入力してください。',
          })
          return z.NEVER
        }
        return parsed
      })
      .nullable(),
  })
  const parsed = schema.safeParse(data)

  if (!parsed.success) {
    return {
      success: false as const,
      message: parsed.error.issues
        .map((x) => {
          const label = csvColumnLabelFor(shearingCertificateCsvColumnMapper, x.path.join(''))
          const value = data[x.path.join('') as keyof typeof data]
          return `${label}: ${x.message}(入力された値: ${value})`
        })
        .join('。'),
      record,
      index,
    }
  }

  const dateParsed = dateUtil.parseDateFormatString(parsed.data.issuedDate)
  if (isNull(dateParsed)) {
    return {
      success: false as const,
      message: `発行日: 日付形式がYYYY/MM/DDの形式になっていません。入力：${parsed.data.issuedDate}`,
      record,
      index,
    }
  }

  const result = {
    issuingCompanyName: parsed.data.issuingCompanyName,
    issuedDate: dateParsed.date,
    certificateNo: parsed.data.certificateNo,
    constructionName: parsed.data.constructionName ?? '',
    steelType: parsed.data.steelType ?? '',
    thickness: parsed.data.thickness ?? '',
    weight: parsed.data.weight,
    no: parsed.data.no ?? '',
    individualNumber: parsed.data.individualNumber ?? '',
    manufacturerName: parsed.data.manufacturerName ?? '',
    rank: index + 1,
    // グルーピングキーの算出に使う発行日は YYYY/MM/DD の文字列で保持
    groupKey: shearingCertificateGroupKey({
      certificateNo: parsed.data.certificateNo,
      issuingCompanyName: parsed.data.issuingCompanyName,
      issuedDate: parsed.data.issuedDate,
      constructionName: parsed.data.constructionName ?? '',
    }),
    raw: data,
  }

  return {
    success: true as const,
    record,
    index,
    result,
    resultTitle: result.individualNumber === '' ? result.no : result.individualNumber,
  }
}
